import React, { useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { ZONE_TYPES } from '../../../hooks/admin/useAdminCommissions';
import {
  CountrySelector,
  StateSelector,
  CitySelector,
  Country,
  State,
  City,
} from '../../common';
import {
  INK,
  FONTS,
  ledgerInputSx,
  ledgerSelectSx,
  ledgerChipSx,
  fieldLabelSx,
} from '../../../styles/ledger';

export interface ZoneEntry {
  type: string;
  id: number;
  data?: { label?: string; [key: string]: any };
}

interface ZoneBuilderProps {
  direction: 'origin' | 'destination';
  zones: ZoneEntry[];
  onChange: (zones: ZoneEntry[]) => void;
  disabled?: boolean;
}

const zoneTypeLabels: Record<string, string> = Object.fromEntries(
  ZONE_TYPES.map((z) => [z.value, z.label])
);

const formatZoneLabel = (
  zoneType: string,
  country?: Country | null,
  state?: State | null,
  city?: City | null
): string => {
  const prefix = zoneTypeLabels[zoneType] || zoneType;
  if (zoneType === 'country') return `${prefix}: ${country?.name}`;
  if (zoneType === 'state') return `${prefix}: ${state?.name}${country ? `, ${country.name}` : ''}`;
  if (zoneType === 'city') {
    const parts = [city?.name];
    if (state?.name) parts.push(state.name);
    if (country?.name) parts.push(country.name);
    return `${prefix}: ${parts.filter(Boolean).join(' • ')}`;
  }
  return prefix;
};

/**
 * Multi-zone picker (country/state/city). A zone is added to the chip list as
 * soon as its location is fully selected — no separate "Add" click needed.
 */
const ZoneBuilder: React.FC<ZoneBuilderProps> = ({ direction, zones, onChange, disabled }) => {
  const [zoneType, setZoneType] = useState('');
  const [country, setCountry] = useState<Country | null>(null);
  const [state, setState] = useState<State | null>(null);
  const [city, setCity] = useState<City | null>(null);

  const label = direction === 'origin' ? 'Origin' : 'Destination';

  const addZone = (type: string, id: number, chipLabel: string) => {
    if (zones.some((z) => z.type === type && z.id === id)) return;
    onChange([...zones, { type, id, data: { label: chipLabel } }]);
  };

  const removeZone = (index: number) => onChange(zones.filter((_, i) => i !== index));

  return (
    <Box
      sx={{
        border: `1px solid ${INK.hairline}`,
        borderRadius: 0.5,
        p: 2.5,
        bgcolor: INK.surface,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: FONTS.mono,
            fontSize: 10,
            letterSpacing: '0.18em',
            color: INK.accent,
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ flex: 1, height: 1, bgcolor: INK.hairlineSoft }} />
        {zoneType && (
          <Typography
            sx={{
              fontFamily: FONTS.mono,
              fontSize: 10,
              color: INK.muted,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {zoneTypeLabels[zoneType] || zoneType}
          </Typography>
        )}
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelSx}>Added zones</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minHeight: 26 }}>
          {zones.length === 0 ? (
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.faint, py: 0.5 }}>
              — none, matches all {direction}s
            </Typography>
          ) : (
            zones.map((zone, index) => (
              <Chip
                key={`${zone.type}-${zone.id}`}
                label={zone.data?.label || `${zoneTypeLabels[zone.type] || zone.type} #${zone.id}`}
                size="small"
                disabled={disabled}
                onDelete={() => removeZone(index)}
                sx={ledgerChipSx}
              />
            ))
          )}
        </Box>
      </Box>

      <Stack spacing={1.5}>
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel
            sx={{
              fontFamily: FONTS.sans,
              fontSize: 13,
              color: INK.muted,
              '&.Mui-focused': { color: INK.accent },
            }}
          >
            Zone type
          </InputLabel>
          <Select
            value={zoneType}
            label="Zone type"
            sx={ledgerSelectSx}
            onChange={(e) => {
              setZoneType(e.target.value);
              setCountry(null);
              setState(null);
              setCity(null);
            }}
          >
            <MenuItem value="">Any zone</MenuItem>
            {ZONE_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(zoneType === 'country' || zoneType === 'state' || zoneType === 'city') && (
          <CountrySelector
            value={country}
            disabled={disabled}
            onChange={(value) => {
              const c = value as Country | null;
              setState(null);
              setCity(null);
              if (zoneType === 'country') {
                if (c) addZone('country', c.id, formatZoneLabel('country', c));
                setCountry(null); // chip added; clear for the next pick
              } else {
                setCountry(c);
              }
            }}
            label="Country"
            showFlag
            textFieldProps={{ size: 'small', sx: ledgerInputSx }}
          />
        )}

        {zoneType === 'state' && country && (
          <StateSelector
            value={state}
            disabled={disabled}
            onChange={(value) => {
              const s = value as State | null;
              if (s) {
                addZone('state', s.id, formatZoneLabel('state', country, s));
                setState(null); // chip added; keep country for more states
              } else {
                setState(s);
              }
            }}
            countryId={country.id}
            label="State"
            textFieldProps={{ size: 'small', sx: ledgerInputSx }}
          />
        )}

        {zoneType === 'city' && country && (
          <>
            <StateSelector
              value={state}
              disabled={disabled}
              onChange={(value) => {
                setState(value as State | null);
                setCity(null);
              }}
              countryId={country.id}
              label="State (optional)"
              textFieldProps={{ size: 'small', sx: ledgerInputSx }}
            />
            <CitySelector
              value={city}
              disabled={disabled}
              onChange={(value) => {
                const c = value as City | null;
                if (c) {
                  addZone('city', c.id, formatZoneLabel('city', country, state, c));
                  setCity(null); // chip added; keep country/state for more cities
                } else {
                  setCity(c);
                }
              }}
              stateId={state?.id}
              countryId={country.id}
              label="City"
              textFieldProps={{ size: 'small', sx: ledgerInputSx }}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ZoneBuilder;
